import React from 'react';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import SPADialog from '@/components/admin/SysadminSPA/shared/SPADialog';
import SPAPill from '@/components/admin/SysadminSPA/shared/SPAPill';
import { SPAThemeScope } from '@/components/admin/SysadminSPA/shared/SPAThemeScope';
import cn from '@/utils/cn';
import ExpandedNotes from './ExpandedNotes';
import SlideAdminNotesDialog from './SlideAdminNotesDialog';
import { SlideStatus, useSlideStatuses } from './useSlideStatuses';

interface SlideAdminNotesProps {
  /** Show every note in the cell instead of the newest one's preview (on slides with several notes). */
  expanded?: boolean;
  initialNotes?: SlideStatus[];
  slideId: number;
  slidePosition: number;
  slideTitle: string;
  stafferId: number;
  statuses: string[];
}

const triggerClass = cn(
  'tw-inline-flex tw-items-start tw-gap-1.5 tw-border-0 tw-bg-transparent tw-p-0 tw-cursor-pointer',
  'tw-text-sm tw-text-spa-accent-text hover:tw-text-spa-accent-text-hover tw-no-underline hover:tw-underline',
  'tw-underline-offset-2',
  'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-spa-accent',
  'focus-visible:tw-outline-offset-2',
);

type NotesTriggerProps = Pick<SlideAdminNotesProps, 'expanded' | 'initialNotes' | 'slideId'>;

const NotesTrigger = ({ expanded = false, initialNotes, slideId }: NotesTriggerProps) => {
  const { data: notes } = useSlideStatuses(slideId, initialNotes);

  if (!notes) return null;

  const newest = notes[0];
  const showAll = expanded && notes.length > 1;

  if (!newest) {
    // Padded by the count pill's width so the text lines up with the previews on the other rows.
    return (
      <SPADialog.Trigger
        className={cn(triggerClass, 'tw-pl-[2.25rem] tw-text-spa-secondary hover:tw-text-spa-accent-text')}
      >
        Add note
      </SPADialog.Trigger>
    );
  }

  return (
    // Expanded, the list takes the cell's full width and the whole block still opens the dialog; the
    // underline is off so a hover doesn't underline every note.
    <SPADialog.Trigger
      aria-label={`${notes.length} Notes`}
      className={cn(
        triggerClass,
        showAll && 'tw-flex tw-w-full tw-flex-col tw-items-stretch tw-gap-0 hover:tw-no-underline',
      )}
      title={showAll ? undefined : newest.note ?? undefined}
    >
      {showAll ? <ExpandedNotes notes={notes} /> : (
        <>
          {/* Pill and check sit in one line-height box so they centre on the first line of a wrapping note. */}
          <span className="tw-inline-flex tw-h-5 tw-shrink-0 tw-items-center tw-gap-1.5">
            <SPAPill tone="accent">{notes.length}</SPAPill>
            {newest.completed_at && (
              <i aria-label="Done" className="fa fa-check-circle tw-text-spa-success tw-leading-none" title="Done" />
            )}
          </span>
          {newest.note
            ? <span className="tw-text-left">{newest.note}</span>
            : <span className="tw-italic tw-text-spa-tertiary">No note text</span>}
        </>
      )}
    </SPADialog.Trigger>
  );
};

// AIDEV-NOTE: SPA components on a legacy /admin page, the way admin/UserSearch is mounted: the
// tokens are pinned light to match the legacy page layout, and SPAThemeScope carries the class to the
// portalled dialog. Mounted once per slide row; SlideStatusPill in the same row shares the cache.
const THEME_CLASS = 'spa-theme-light';

/** The trigger and dialog alone, for a parent that already provides the query client and theme scope. */
export const SlideAdminNotesCell = ({
  expanded, initialNotes, slideId, ...dialogProps
}: SlideAdminNotesProps) => (
  <SPADialog>
    <NotesTrigger expanded={expanded} initialNotes={initialNotes} slideId={slideId} />
    <SlideAdminNotesDialog initialNotes={initialNotes} slideId={slideId} {...dialogProps} />
  </SPADialog>
);

const SlideAdminNotes = (props: SlideAdminNotesProps) => (
  <ReactQueryProvider>
    <SPAThemeScope className={THEME_CLASS}>
      <span className={cn(THEME_CLASS, 'tw-font-tci')}>
        <SlideAdminNotesCell {...props} />
      </span>
    </SPAThemeScope>
  </ReactQueryProvider>
);

export default SlideAdminNotes;
